import { generatePath } from "react-router-dom";

import { URLS } from "@/api/urls";
import { Channel, ChannelDetailEpisode } from "@/types/Channel";

import fetcher_v2 from "../fetcher_v2";

export const CHANNEL_LIST_INCLUDE = {
  EPISODE_COUNT: "episode_count",
  LATEST_PUBLISHED_DATE: "latest_published_date",
};
export const CHANNEL_DETAIL_INCLUDE = {
  EPISODE_COUNT: "episode_count",
  LATEST_PUBLISHED_DATE: "latest_published_date",
  TRANSCRIPTION_STATUS: "transcription_status",
  AUTOMATIC_TRANSCRIPTION: "automatic_transcription",
  INTERNAL_STATISTICS: "internal_statistics",
};
export const EPISODE_LIST_INCLUDE = {
  MENTION_COUNT: "mention_count",
  LATEST_TRANSCRIPT_STATUS: "latest_transcript_status",
};
export const EPISODE_DETAIL_INCLUDE = {
  MENTION_COUNT: "mention_count",
  LATEST_TRANSCRIPT: "latest_transcript",
  TRANSCRIPT_PART_COUNT: "transcript_part_count",
  REQUESTED_BY_USER: "requested_by_user",
  TRANSCRIPT_TEXT: "transcript_text",
};
export const EPISODE_DETAIL_VIEW_ACTION = {
  TRANSCRIBE: "transcribe",
};
export const EPISODE_ACTION_RESPONSE_STATUS = {
  TRANSCRIPTION_STARTED: "Transcription started",
};

export const CHANNEL_DETAIL_VIEW_ACTION = {
  TRANSCRIBE: "transcribe",
  REINDEX_EPISODES: "reindex_episodes",
};

export type ChannelDetailViewAction =
  (typeof CHANNEL_DETAIL_VIEW_ACTION)[keyof typeof CHANNEL_DETAIL_VIEW_ACTION];

interface ChannelActionResponse {
  status?: string;
}

export type ChannelListInclude =
  (typeof CHANNEL_LIST_INCLUDE)[keyof typeof CHANNEL_LIST_INCLUDE];
export type ChannelDetailInclude =
  (typeof CHANNEL_DETAIL_INCLUDE)[keyof typeof CHANNEL_DETAIL_INCLUDE];
export type EpisodeListInclude =
  (typeof EPISODE_LIST_INCLUDE)[keyof typeof EPISODE_LIST_INCLUDE];
export type EpisodeDetailInclude =
  (typeof EPISODE_DETAIL_INCLUDE)[keyof typeof EPISODE_DETAIL_INCLUDE];
export type EpisodeDetailViewAction =
  (typeof EPISODE_DETAIL_VIEW_ACTION)[keyof typeof EPISODE_DETAIL_VIEW_ACTION];
export type EpisodeActionResponseStatus =
  (typeof EPISODE_ACTION_RESPONSE_STATUS)[keyof typeof EPISODE_ACTION_RESPONSE_STATUS];

interface ChannelListData {
  results: Channel[];
  count: number;
  page: number;
  page_count: number;
}

interface EpisodeListData {
  // Placed here and not in episodes.ts to gather the internal API data in one place
  results: ChannelDetailEpisode[];
  count: number;
  page: number;
  page_count: number;
}

interface EpisodeActionResponse {
  status?: EpisodeActionResponseStatus;
}

export type ChannelListFilters = {
  // Filters
  name?: string;
  channel_type?: string;
  language?: string;
  // Ordering
  ordering?: string;
  // Meta
  page?: number;
  page_size?: number;
};

export type EpisodeListFilters = {
  // Ordering
  ordering?: string;
  // Meta
  page?: number;
  page_size?: number;
};

export const getChannelListData = (
  filters?: ChannelListFilters,
  includes?: ChannelListInclude[]
) => {
  return fetcher_v2<ChannelListData>(URLS.INTERNAL_CHANNEL_LIST, {
    body: {
      includes: includes || [],
      ...(filters || {}),
    },
    method: "GET",
  });
};

export const getChannelDetailData = (
  id: string,
  includes?: ChannelDetailInclude[]
) => {
  return fetcher_v2<Channel>(
    generatePath(URLS.INTERNAL_CHANNEL_DETAIL, { id }),
    {
      body: {
        includes: includes || [],
      },
      method: "GET",
    }
  );
};

export const patchChannel = (
  id: string,
  updated_data: Partial<Omit<Channel, "internal_statistics">>
) => {
  return fetcher_v2<Channel>(
    generatePath(URLS.INTERNAL_CHANNEL_DETAIL, { id }),
    {
      method: "PATCH",
      body: updated_data,
    }
  );
};

export const getEpisodeListData = (
  channel_id: string,
  filters?: EpisodeListFilters,
  includes?: EpisodeListInclude[]
) => {
  return fetcher_v2<EpisodeListData>(URLS.INTERNAL_EPISODE_LIST, {
    body: {
      includes: includes || [],
      channel_id,
      ...(filters || {}),
    },
    method: "GET",
  });
};

export const getEpisodeDetailData = (
  id: string,
  includes?: EpisodeDetailInclude[]
) => {
  return fetcher_v2<ChannelDetailEpisode>(
    generatePath(URLS.INTERNAL_EPISODE_DETAIL, { id }),
    {
      body: {
        includes: includes || [],
      },
      method: "GET",
    }
  );
};

export const postEpisodeAction = (
  id: string,
  action: EpisodeDetailViewAction
) => {
  return fetcher_v2<EpisodeActionResponse>(
    generatePath(URLS.INTERNAL_EPISODE_DETAIL, { id }),
    {
      method: "POST",
      body: { action },
    }
  );
};

export const postChannelAction = (
  id: string,
  action: ChannelDetailViewAction
) => {
  return fetcher_v2<
    ChannelActionResponse,
    { action: ChannelDetailViewAction },
    { error: string }
  >(generatePath(URLS.INTERNAL_CHANNEL_DETAIL, { id }), {
    method: "POST",
    body: { action },
  });
};
